import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import Typography from "style-guide/component/Typography";
import { useWebSocket } from "api/websocket";
import { parseTextWithEntities, updateEntityInText } from "./EditableText";
import { INTAKE_FLOW_SCRATCH } from "components/dashboard/home/constants";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "redux/store";
import { uploadExistingRfp } from "../../../../redux/slice/rfpModuleSlice/build";
import Loading from "style-guide/component/Loader";

// Custom hooks
import { useQuestionState } from "./hooks/useQuestionState";
import { useFileManagement } from "./hooks/useFileManagement";

// Components
import QuestionTextEditor from "./components/QuestionTextEditor";
import QuestionFileUpload from "./components/QuestionFileUpload";
import FileList from "./components/FileList";
import ExamplePopup from "./components/ExamplePopup";
import DeleteModal from "./components/DeleteModal";
import QuestionNavigation from "./components/QuestionNavigation";

// Utils
import { fileOperations } from "./utils/fileUtils";

// Types
import { QuestionProps } from "./types/questionTypes";

const NewQuestion: React.FC<QuestionProps> = ({
  refine,
  currentQuestion,
  setCurrentQuestion,
  setSearchParams,
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { clearMessage } = useWebSocket();
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading } = useSelector((state: RootState) => state.applicationData);
  const { listOfFiles } = useSelector((state: RootState) => state.overviewData);

  // Custom hooks for state management
  const questionState = useQuestionState({
    currentQuestion,
    setCurrentQuestion,
    setSearchParams,
    refine,
  });

  const fileManagement = useFileManagement({ currentQuestion });

  // Derived state
  const currentFileList = fileManagement.questionFiles[currentQuestion] || [];
  const allFilesForCurrentQuestion = fileOperations.getAllFilesForCurrentQuestion(
    listOfFiles,
    currentFileList,
    currentQuestion
  );
  const allExamples = (refine[currentQuestion]?.examples || []).join("\n");
  const parsedContent = !questionState.edit ? parseTextWithEntities(questionState.textValue) : [];

  // File upload handler
  const uploadCurrentQuestionFiles = async (): Promise<boolean> => {
    const files = fileOperations.getNewFilesToUpload(
      listOfFiles,
      fileManagement.questionFiles,
      currentQuestion
    );
    if (files.length === 0) return true;
    
    try {
      fileManagement.setUploading(true);
      const response = await dispatch(uploadExistingRfp(files));
      return response === 200 || response === 201;
    } finally {
      fileManagement.setUploading(false);
    }
  };

  // Navigation handlers
  const handleNext = async () => {
    questionState.saveAnswer(questionState.textValue);
    const ok = await uploadCurrentQuestionFiles();
    if (!ok) return;
    questionState.proceedToNextQuestion();
  };

  const handleBack = () => {
    questionState.saveAnswer(questionState.textValue);
    if (currentQuestion > 0) {
      const previousQuestion = currentQuestion - 1;
      setCurrentQuestion(previousQuestion);
    } else {
      localStorage.setItem(INTAKE_FLOW_SCRATCH, "requirement-add");
      navigate("/build-scratch");
    }
  };

  const handleSave = async () => {
    clearMessage();
    questionState.saveAnswer(questionState.textValue);

    const isLast = currentQuestion === refine.length - 1;
    if (isLast) {
      const ok = await uploadCurrentQuestionFiles();
      if (!ok) return;
      localStorage.setItem(INTAKE_FLOW_SCRATCH, "refined-requirements");
      navigate("/build-scratch");
      return;
    }

    const nextQuestion = currentQuestion + 1;
    setCurrentQuestion(nextQuestion);
    setSearchParams({ currentQuestion: nextQuestion.toString() });
    localStorage.setItem("currentQuestion", nextQuestion.toString());
    questionState.setIsPopupOpen(true);
  };

  // Entity update handler
  const handleEntityUpdate = (entityIndex: number, newValue: string) => {
    const updatedText = updateEntityInText(questionState.textValue, entityIndex, newValue);
    questionState.handleTextChange(updatedText);
  };

  // Delete confirmation handler
  const handleConfirmDelete = async () => {
    await fileManagement.handleConfirmDelete(dispatch);
  };

  return (
    <>
      {/* Loading spinner for delete */}
      {fileManagement.isDelete && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999] bg-black bg-opacity-30">
          <div
            className="w-10 h-10 border-4 border-t-blue-primary border-solid rounded-full animate-spin bg-white"
            role="status"
          />
        </div>
      )}

      <div className="flex flex-col gap-4 w-full max-w-[800px] relative pb-20">
        {/* Loading overlay */}
        {(fileManagement.uploading || isLoading) && (
          <div className="fixed inset-0 flex items-center justify-center z-[9999] text-center">
            <Loading mainHeading={fileManagement.uploading ? "Uploading files" : "Loading"} />
          </div>
        )}

        {/* Question title */}
        <Typography
          variant="Heading 4"
          text={refine[currentQuestion]?.what_to_provide}
        />

        {/* Text editor */}
        <div
          className="relative w-full flex flex-col gap-8"
          ref={divRef}
          onClick={() => questionState.setEdit(true)}
        >
          <QuestionTextEditor
            edit={questionState.edit}
            textValue={questionState.textValue}
            placeholder={allExamples}
            onTextChange={questionState.handleTextChange}
            onSetEdit={questionState.setEdit}
            parsedContent={parsedContent}
            onEntityUpdate={handleEntityUpdate}
          />
        </div>

        {/* File upload section */}
        <QuestionFileUpload
          currentFileList={currentFileList}
          beforeUpload={fileManagement.beforeUpload}
          handleFilesChange={fileManagement.handleFilesChange}
          fileTypeError={fileManagement.fileTypeError}
        />

        {/* File list */}
        <FileList
          files={allFilesForCurrentQuestion}
          currentQuestion={currentQuestion}
          onRemoveFile={fileManagement.handleRemoveFile}
        />

        {/* Example popup */}
        <ExamplePopup
          visible={questionState.isPopupOpen}
          examples={refine[currentQuestion]?.examples || []}
          onUseExample={questionState.handleUseExample}
          onClose={() => questionState.setIsPopupOpen(false)}
        />

        {/* Navigation */}
        <QuestionNavigation
          currentQuestion={currentQuestion}
          totalQuestions={refine.length}
          onBack={handleBack}
          onNext={handleNext}
          onSave={handleSave}
          isLoading={fileManagement.uploading || isLoading}
        />

        {/* Delete modal */}
        <DeleteModal
          visible={fileManagement.deleteModal.visible}
          file={fileManagement.deleteModal.file}
          uploadedFile={fileManagement.deleteModal.uploadedFile}
          deleteModalType={fileManagement.deleteModalType}
          isDelete={fileManagement.isDelete}
          deleteError={fileManagement.deleteError}
          onConfirm={handleConfirmDelete}
          onCancel={fileManagement.handleCancelDelete}
        />
      </div>
    </>
  );
};

export default NewQuestion;